'use client'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import Link from 'next/link'
import useEmblaCarousel from 'embla-carousel-react'

import { cn } from '@/utilities/ui'
import { outfit } from '@/fonts'

export type BlogCarouselItem = {
  id: number
  title: string
  imageUrl: string
  imageAlt?: string | null
  href?: string | null
  publishedMonthYear?: string
}

const IMG_W = 500
const IMG_H = 400

/** Same timing constants as WhySectionProjectSlider */
const AUTOPLAY_SCROLL_MS = 6000
const AUTOPLAY_PAUSE_MS = 3000
/** Arrow-button click animation */
const TRANSITION_MS = 450

function renderHeadingWithBreaks(text: string): React.ReactNode {
  const normalized = text.replace(/<br\s*\/?>/gi, '\n')
  const lines = normalized.split('\n')

  return lines.map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {line}
      {index < lines.length - 1 ? <br /> : null}
    </React.Fragment>
  ))
}

function visibleSlidesForViewportWidth(vw: number): number {
  if (vw < 640) return 1
  if (vw < 1280) return 2
  return 3
}

function gapForViewportWidth(_vw: number): number {
  return 20
}

function CardLink({ href, children }: { href?: string | null; children: React.ReactNode }) {
  const raw = typeof href === 'string' ? href.trim() : ''
  if (!raw) {
    return <div className="flex min-w-0 flex-col items-center gap-2" draggable={false}>{children}</div>
  }
  const internal = raw.startsWith('/')
  if (internal) {
    return (
      <Link
        className="flex min-w-0 flex-col items-center gap-2 text-inherit no-underline"
        draggable={false}
        href={raw}
      >
        {children}
      </Link>
    )
  }
  return (
    <a
      className="flex min-w-0 flex-col items-center gap-2 text-inherit no-underline"
      draggable={false}
      href={raw}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  )
}

export const HomepageBlogCarouselClient: React.FC<{
  heading: string
  items: BlogCarouselItem[]
}> = ({ heading, items }) => {
  // ─── Embla (handles drag + pointer capture reliably) ───────────────────────
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false, // Disabled native loop to simplify custom CSS transition
    startIndex: items.length > 0 ? items.length * 20 : 0, // Start in the middle so user can drag left or right
    align: 'start',
    duration: TRANSITION_MS, // used by arrow-button clicks
    slidesToScroll: 1,
  })

  // ─── Slide geometry (measured from Embla's root node) ─────────────────────
  const [slideWidth, setSlideWidth] = useState(0)
  const [gapPx, setGapPx] = useState(20)
  const [visibleCount, setVisibleCount] = useState(2)

  useLayoutEffect(() => {
    if (!emblaApi) return
    const root = emblaApi.rootNode()

    const measure = () => {
      const trackW = root.getBoundingClientRect().width
      const vw = window.innerWidth
      const vis = visibleSlidesForViewportWidth(vw)
      const g = gapForViewportWidth(vw)
      const sw = trackW > 0 ? Math.max(0, (trackW - g * (vis - 1)) / vis) : 0
      setSlideWidth(sw)
      setGapPx(g)
      setVisibleCount(vis)
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(root)
    window.addEventListener('resize', measure)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [emblaApi])

  const stepPx = slideWidth + gapPx

  // ─── Reduce motion ─────────────────────────────────────────────────────────
  const [reduceMotion, setReduceMotion] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  // ─── Custom slow autoplay (CSS transition on Embla's container node) ────────
  const autoplayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!emblaApi || reduceMotion || stepPx <= 0 || items.length <= 1) return

    let active = true

    function clear() {
      if (autoplayTimerRef.current) {
        clearTimeout(autoplayTimerRef.current)
        autoplayTimerRef.current = null
      }
    }

    function scheduleNext() {
      if (!active) return
      clear()
      autoplayTimerRef.current = setTimeout(scroll, AUTOPLAY_PAUSE_MS)
    }

    function scroll() {
      if (!active || !emblaApi) return

      const container = emblaApi.containerNode()

      // Read current X from live computed style (works even if Embla or a
      // previous CSS animation already moved the container)
      const matrix = new DOMMatrix(getComputedStyle(container).transform)
      const currentX = matrix.m41 // translateX component in px

      // Apply the same slow deceleration as WhySectionProjectSlider
      container.style.transition = `transform ${AUTOPLAY_SCROLL_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
      container.style.transform = `translate3d(${currentX - stepPx}px, 0, 0)`

      autoplayTimerRef.current = setTimeout(() => {
        if (!active || !emblaApi) return

        // ── Sync Embla's internal index after the animation ──
        // 1. Kill the transition so the snap is invisible
        container.style.transition = 'none'

        // 2. Instantly move Embla to the next snap (true = jump, no Embla animation)
        emblaApi.scrollNext(true)

        scheduleNext()
      }, AUTOPLAY_SCROLL_MS)
    }

    const onPointerDown = () => {
      active = false
      clear()
      const container = emblaApi.containerNode()
      container.style.transition = 'none'
    }

    const onSettle = () => {
      active = true
      scheduleNext()
    }

    emblaApi.on('pointerDown', onPointerDown)
    emblaApi.on('settle', onSettle)

    scheduleNext()

    return () => {
      active = false
      clear()
      emblaApi.off('pointerDown', onPointerDown)
      emblaApi.off('settle', onSettle)
      // Stop any in-progress CSS transition cleanly
      if (emblaApi) {
        const container = emblaApi.containerNode()
        container.style.transition = 'none'
      }
    }
  }, [emblaApi, reduceMotion, stepPx, items.length])

  // Cancel autoplay when user interacts via arrow buttons
  const handleScrollPrev = useCallback(() => {
    if (!emblaApi) return
    // Kill CSS autoplay transition before Embla takes over
    const container = emblaApi.containerNode()
    container.style.transition = 'none'
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
      autoplayTimerRef.current = null
    }
    emblaApi.scrollPrev()
  }, [emblaApi])

  const handleScrollNext = useCallback(() => {
    if (!emblaApi) return
    const container = emblaApi.containerNode()
    container.style.transition = 'none'
    if (autoplayTimerRef.current) {
      clearTimeout(autoplayTimerRef.current)
      autoplayTimerRef.current = null
    }
    emblaApi.scrollNext()
  }, [emblaApi])

  // ─── Slide item width (CSS) ────────────────────────────────────────────────
  const slideItemStyle = useMemo((): React.CSSProperties => {
    const v = Math.max(1, visibleCount)
    const g = gapPx
    if (slideWidth > 0) return { width: slideWidth, flexShrink: 0 }
    return { width: `calc((100% - ${g * (v - 1)}px) / ${v})`, flexShrink: 0 }
  }, [slideWidth, gapPx, visibleCount])

  // ─── Duplicated Items for Infinite Scroll without Embla Loop ───────────────
  const displayItems = useMemo(() => {
    if (items.length <= 1) return items
    const repeatCount = 40 // Duplicate enough times to last virtually forever
    const result: (BlogCarouselItem & { originalIndex: number })[] = []
    for (let i = 0; i < repeatCount; i++) {
      items.forEach((item, index) => {
        result.push({ ...item, originalIndex: index })
      })
    }
    return result
  }, [items])

  if (items.length === 0) return null

  const showArrows = items.length > 1

  return (
    <section className="relative max-w-[1140px] min-h-[75vh] flex flex-col justify-evenly mx-auto w-full px-[10px] py-[20px] md:py-[10px] 2xl:px-0">
      <div className="container-fluid mb-[20px] min-[1440px]:mb-[40px]">
        <h2 className="text-[34px] font-normal leading-[1.2em] text-[#222]">
          {renderHeadingWithBreaks(heading)}
        </h2>
        <div aria-hidden className="mt-[1px] h-2.5 w-[175px] md:w-[40%] bg-[#1595a7]" />
      </div>

      <div className="container-fluid relative pb-[20px]">
        {showArrows ? (
          <>
            <button
              type="button"
              className={cn(
                'absolute left-0 top-1/2 z-10 flex h-[40px] w-[40px] -translate-y-1/2 items-center justify-center transition-opacity hover:opacity-70'
              )}
              style={{ color: 'var(--arrow-normal-color, hsla(0,0%,93%,.9))' }}
              aria-label="Previous post"
              onClick={handleScrollPrev}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                fill="currentColor"
                width={24}
                height={24}
                aria-hidden
              >
                <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
              </svg>
            </button>
            <button
              type="button"
              className={cn(
                'absolute right-0 top-1/2 z-10 flex h-[40px] w-[40px] -translate-y-1/2 items-center justify-center transition-opacity hover:opacity-70'
              )}
              style={{ color: 'var(--arrow-normal-color, hsla(0,0%,93%,.9))' }}
              aria-label="Next post"
              onClick={handleScrollNext}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                fill="currentColor"
                width={24}
                height={24}
                aria-hidden
              >
                <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L243.2 256 73.9 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
              </svg>
            </button>
          </>
        ) : null}

        {/* Embla viewport — overflow:hidden required by Embla */}
        <div
          ref={emblaRef}
          className="min-h-0 w-full min-w-0 overflow-hidden cursor-grab active:cursor-grabbing select-none"
          style={{ touchAction: 'pan-y' }}
        >
          {/* Embla container — Embla owns transform; autoplay overlays via CSS */}
          <div
            className="flex will-change-transform"
            style={{ gap: gapPx }}
          >
            {displayItems.map((item, i) => (
              <div
                key={`${item.id}-${i}`}
                className="box-border min-w-0 shrink-0"
                style={slideItemStyle}
              >
                <CardLink href={item.href}>
                  <div className="relative mx-auto h-[500px] w-full min-w-0 overflow-hidden bg-[#e8e8e8]">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      alt={item.imageAlt || item.title}
                      className="absolute inset-0 z-0 h-full w-full object-cover"
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                      height={IMG_H}
                      src={item.imageUrl}
                      width={IMG_W}
                    />
                    
                    <div className="absolute left-0 right-0 top-0 z-10 flex h-[80px] items-center justify-between gap-2 bg-white/80 ">
                      <div className="flex items-start flex-col gap-0">
                        <Image
                          alt="Craft"
                          className="h-12 w-auto object-cover object-left"
                          height={50}
                          src="/logo/logo.svg"
                          width={110}
                          draggable={false}
                          onDragStart={(e) => e.preventDefault()}
                        />
                        <span
                          className={cn(
                            outfit.className,
                            'text-[18px] font-light leading-[22px] text-[rgb(29,29,27)]',
                          )}
                        >
                          MAGAZINE
                        </span>
                      </div>
                      {item.publishedMonthYear ? (
                        <p
                          className={cn(
                            outfit.className,
                            'absolute top-[5px] right-[5px] text-[16px] font-normal leading-[24px] text-[rgb(34,34,34)]',
                          )}
                        >
                          {item.publishedMonthYear}
                        </p>
                      ) : null}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 z-10 bg-[#1595a7] px-3 py-3 sm:px-4 sm:py-4">
                      <p
                        className="text-center italic font-medium text-white text-[25px] leading-[30px]"
                        style={{ fontFamily: 'var(--font-gfs-didot), "GFS Didot", serif' }}
                      >
                        {item.title}
                      </p>
                    </div>
                  </div>
                </CardLink>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
